import { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../../../lib/api';

interface FeedbackItem {
  _id: string;
  user?: { name: string; email: string } | null;
  sessionId?: string;
  message: string;
  answer: string;
  citations?: any[];
  rating: 'up' | 'down';
  createdAt: string;
}

const ChatFeedbackPage = () => {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [rating, setRating] = useState<'all' | 'up' | 'down'>('all');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.client.get<any>('/chat/feedback', {
        params: { page, limit: 20, rating: rating === 'all' ? undefined : rating },
        useCache: false
      });
      if (res.success && res.data) {
        setItems(res.data);
        setPages(res.pagination?.pages || 1);
      } else {
        setError(res.error?.message || 'Error al cargar feedback');
      }
    } catch (e: any) {
      setError(e.message || 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rating]);

  return (
    <AdminLayout title="Feedback del Chat">
      <div className="bg-white p-4 rounded-lg shadow mb-4 flex items-center gap-3">
        <label className="text-sm text-gray-700">Filtrar:</label>
        <select
          value={rating}
          onChange={(e) => { setPage(1); setRating(e.target.value as any); }}
          className="border border-gray-300 rounded-md px-2 py-1 text-sm"
        >
          <option value="all">Todos</option>
          <option value="up">Útil</option>
          <option value="down">No útil</option>
        </select>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-6 text-center text-gray-500">Cargando…</div>
        ) : error ? (
          <div className="p-6 text-center text-red-600">{error}</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mensaje</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Citas</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((fb) => (
                <tr key={fb._id} className="align-top">
                  <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                    {new Date(fb.createdAt).toLocaleString('es-ES')}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    {fb.user ? `${fb.user.name} (${fb.user.email})` : fb.sessionId || '—'}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    <div className="font-medium text-gray-900">Q: {fb.message}</div>
                    <div className="mt-1 text-gray-700">A: {fb.answer}</div>
                  </td>
                  <td className="px-4 py-2 text-sm">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${fb.rating === 'up' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {fb.rating === 'up' ? 'Útil' : 'No útil'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-500">
                    {(fb.citations || []).slice(0, 3).map((c, i) => (
                      <div key={i} className="truncate">
                        {c?.payload?.name || c?.payload?.title || c?.payload?.text?.slice(0, 60) || `doc${i + 1}`}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={5}>Sin feedback</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginación */}
      <div className="mt-4 flex items-center justify-between">
        <button
          className="px-3 py-1 text-sm border rounded disabled:opacity-50"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
        >Anterior</button>
        <div className="text-sm text-gray-600">Página {page} de {pages}</div>
        <button
          className="px-3 py-1 text-sm border rounded disabled:opacity-50"
          onClick={() => setPage((p) => Math.min(pages, p + 1))}
          disabled={page >= pages}
        >Siguiente</button>
      </div>
    </AdminLayout>
  );
};

export default ChatFeedbackPage;










